/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { CMSImage } from '@/features/contentstack/components/CMSImage/CMSImage';
import { CMSLink } from '@/features/contentstack/components/CMSLink/CMSLink';
import styles from './HorizontalCard.module.scss';
import { type ContentStackHorizontalScrollCard } from '@/features/contentstack/interface';
import { Text } from '@az/starc-ui';
import { useRouter } from 'next/router';
import root from 'window-or-global';

type Props = {
  index: number;
  card: ContentStackHorizontalScrollCard;
  numberOfCards: number;
};

const TITLE_CHARS_PER_LINE = 25;

const defaultSizePerDevice = {
  mobile: {
    width: 140,
    height: 102,
  },
  tablet: {
    width: 188,
    height: 148,
  },
  desktop: {
    width: 220,
    height: 148,
  },
};

const HorizontalCard = (props: Props) => {
  const { index, card } = props;
  const router = useRouter();

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!card.link) {
      return;
    }

    if (/videos|ubicaciones|locations|company/i.test(card.link)) {
      // open external link in new tab
      root.open(card.link, '_blank', 'noopener,noreferrer');
      return;
    }

    void router.push(card.link);
  };

  return (
    <div key={index} className={styles.card} role="listitem" onClick={handleNavigate}>
      <div className={styles.cardBody}>
        <div className={styles.cardText}>
          <CMSLink to={card.link || ''}>
            <Text className={styles.heading} maxLines={2} as="h3">
              {card.heading}
            </Text>
          </CMSLink>
          <Text
            maxLines={!!card.heading?.length && card.heading.length > TITLE_CHARS_PER_LINE ? 1 : 2}
            className={styles.subheading}
          >
            {card.sub_heading}
          </Text>
        </div>

        {card.image && (
          <div className={styles.imageContainer}>
            <CMSImage image={card.image} sizePerDevice={defaultSizePerDevice} />
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalCard;
